import styled, { createGlobalStyle } from 'styled-components';

export const GlobalStyle = createGlobalStyle`
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  input[type=text], input[type=email], input[type=tel] { -webkit-appearance: none; }
  input:focus, textarea:focus { outline: none; }
  input:focus-visible, button:focus-visible {
    outline: 2px solid #5a8bff;
    outline-offset: 2px;
  }
`;

// ── Page shell ────────────────────────────────────────────────────────────────

export const PageWrap = styled.div`
  height: 100vh;
  overflow-y: auto;
  background: #000;
  font-family: 'Roboto Condensed', sans-serif;
  padding-bottom: 80px;
`;

export const Container = styled.div`
  max-width: 680px;
  margin: 0 auto;
  padding: 0 20px;
  @media (max-width: 480px) { max-width: 390px; }
`;

export const TopBar = styled.div`
  padding: 28px 0 22px;
  border-bottom: 1px solid #15191f;
`;

export const Logo = styled.img`
  height: 34px;
  width: auto;
`;

export const FormHeading = styled.div`
  padding: 38px 0 30px;
`;

export const PageTitle = styled.h1`
  font-family: 'Oswald', sans-serif;
  font-weight: 500;
  font-size: 28px;
  line-height: 33.6px;
  letter-spacing: -0.5px;
  color: #fff;
  margin-bottom: 10px;
`;

export const PageSubtitle = styled.p`
  font-size: 17px;
  line-height: 1.5;
  color: #8a94a6;
  max-width: 540px;
`;

export const StatusMsg = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  color: #8a94a6;
  font-size: 17px;
`;

// ── Section ───────────────────────────────────────────────────────────────────

export const Section = styled.div`
  margin-bottom: 34px;
`;

export const SectionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: ${p => p.$compact ? '8px' : '18px'};
`;

export const SectionBullet = styled.div`
  width: 8px;
  height: 8px;
  flex: none;
  background: ${p => p.$color};
`;

export const SectionTitleText = styled.span`
  font-size: 15px;
  line-height: 24px;
  letter-spacing: 0.3px;
  color: #fff;
  white-space: nowrap;
`;

export const SectionDivider = styled.div`
  flex: 1;
  height: 1px;
  background: #15191f;
`;

export const SectionDesc = styled.p`
  font-size: 14px;
  color: #5a6473;
  line-height: 1.5;
  margin-bottom: 18px;
`;

// ── Grid ──────────────────────────────────────────────────────────────────────

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(${p => p.$min || '240px'}, 1fr));
  gap: 18px;
`;

export const FullWidth = styled.div`
  grid-column: 1 / -1;
`;

// ── Fields ────────────────────────────────────────────────────────────────────

export const FieldLabel = styled.label`
  display: block;
  font-size: 12px;
  letter-spacing: 0.6px;
  color: #8a94a6;
  font-weight: 700;
  margin-bottom: 7px;
`;

export const RequiredStar = styled.span`
  color: #f04e23;
`;

export const TextInput = styled.input`
  width: 100%;
  background: #12161e;
  border: 1px solid ${p => p.$err ? '#a83b27' : '#222a37'};
  border-radius: 10px;
  padding: 14px 16px;
  font-size: 17px;
  color: #fff;
  font-family: 'Roboto Condensed', sans-serif;
  &::placeholder { color: #5a6473; opacity: 1; }
`;

export const FieldError = styled.p`
  color: #ff6a4d;
  font-size: 13px;
  margin-top: ${p => p.$tight ? '4px' : '6px'};
`;

// ── Pre-filled display ────────────────────────────────────────────────────────

export const PrefillLabelRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 7px;
`;

export const PrefillLabel = styled.span`
  font-size: 12px;
  letter-spacing: 0.6px;
  color: #8a94a6;
  font-weight: 700;
`;

export const PrefillBadge = styled.span`
  border: 1px solid #232a38;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 10px;
  color: #5a6473;
  letter-spacing: 0.5px;
`;

export const PrefillBox = styled.div`
  background: #0a0d13;
  border: 1px solid #181d27;
  border-radius: 10px;
  padding: 14px 16px;
  font-size: 17px;
  color: #aeb6c4;
`;

// ── Terms ─────────────────────────────────────────────────────────────────────

export const CommissionBox = styled.div`
  background: #0a0d13;
  border: 1px solid #181d27;
  border-radius: 10px;
  padding: 16px 18px;
  font-size: 16px;
  color: #aeb6c4;
  margin-bottom: 18px;
`;

export const TermsLabel = styled.label`
  display: flex;
  gap: 14px;
  align-items: flex-start;
  cursor: pointer;
  padding: 18px;
  border: 1px solid ${p => p.$err ? '#5b291f' : '#181d27'};
  border-radius: 12px;
  background: #0a0d13;
`;

export const CheckboxEl = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 6px;
  flex: none;
  border: 1px solid ${p => p.$checked ? '#1f4fe6' : p.$err ? '#a83b27' : '#39404e'};
  background: ${p => p.$checked ? '#1f4fe6' : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-family: 'Oswald', sans-serif;
  font-weight: 700;
  font-size: 15px;
`;

export const TermsText = styled.span`
  font-size: 15px;
  color: #cfd5df;
  line-height: 1.55;
`;

export const TermsLink = styled.a`
  color: #5a8bff;
  text-decoration: underline;
`;

// ── Submit ────────────────────────────────────────────────────────────────────

export const ErrorBanner = styled.div`
  background: #1a0d0a;
  border: 1px solid #5b291f;
  border-radius: 10px;
  padding: 14px 18px;
  color: #ff8a70;
  font-size: 15px;
  margin-bottom: 18px;
`;

export const SubmitBtn = styled.button`
  width: 100%;
  padding: 20px;
  background: #1f4fe6;
  border: none;
  border-radius: 14px;
  font-family: 'Oswald', sans-serif;
  font-weight: 500;
  font-size: 19px;
  line-height: 24px;
  letter-spacing: 0.5px;
  color: #fff;
  cursor: pointer;
  &:hover { background: #1a45cc; }
  &:disabled {
    background: #9aa3b5;
    cursor: not-allowed;
    &:hover { background: #9aa3b5; }
  }
`;

// ── Confirmation ──────────────────────────────────────────────────────────────

export const ConfirmWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: 62vh;
  padding: 40px 0;
`;

export const CheckCircle = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 999px;
  background: #1f8a5b;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 26px;
  font-family: 'Oswald', sans-serif;
  font-weight: 700;
  font-size: 34px;
  color: #fff;
`;

export const ConfirmTitle = styled.h2`
  font-family: 'Oswald', sans-serif;
  font-weight: 500;
  font-size: 40px;
  line-height: 1.05;
  letter-spacing: -0.5px;
  color: #fff;
  margin-bottom: 16px;
`;

export const ConfirmBody = styled.p`
  font-size: 18px;
  line-height: 1.55;
  color: #8a94a6;
  max-width: 480px;
`;
